import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { driverAPI, documentAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Car, Loader2, Upload, FileText, CheckCircle, XCircle, AlertCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const DOCUMENT_INFO = {
  drivers_license: {
    name: "Driver's License",
    description: "Upload a clear photo of your valid driver's license (front side)",
    icon: FileText,
    accept: "image/*,.pdf"
  },
  vehicle_registration: {
    name: "Vehicle Registration",
    description: "Upload your current vehicle registration document",
    icon: Car,
    accept: "image/*,.pdf"
  },
  insurance: {
    name: "Insurance Certificate",
    description: "Upload your valid auto insurance certificate",
    icon: FileText,
    accept: "image/*,.pdf"
  }
};

const DocumentUploadCard = ({ docType, docInfo, status, onUpload, uploading }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = async (file) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      onUpload(docType, base64, file.name, file.type);
    };
    reader.readAsDataURL(file);
  };

  const getStatusColor = () => {
    switch (status) {
      case 'approved': return 'bg-green-500/20 border-green-500/50 text-green-400';
      case 'pending': return 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400';
      case 'rejected': return 'bg-red-500/20 border-red-500/50 text-red-400';
      default: return 'bg-white/5 border-white/20 text-slate-400';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'pending': return <AlertCircle className="w-5 h-5 text-yellow-400" />;
      case 'rejected': return <XCircle className="w-5 h-5 text-red-400" />;
      default: return <Upload className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'pending': return 'Under Review';
      case 'rejected': return 'Rejected - Please Re-upload';
      default: return 'Not Uploaded';
    }
  };

  const Icon = docInfo.icon;

  return (
    <div
      className={`relative p-4 rounded-lg border-2 border-dashed transition-all ${
        dragActive ? 'border-red-500 bg-red-500/10' : getStatusColor()
      } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={docInfo.accept}
        onChange={(e) => e.target.files[0] && handleFile(e.target.files[0])}
        className="hidden"
        data-testid={`file-input-${docType}`}
      />

      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-lg ${status === 'approved' ? 'bg-green-500/20' : status === 'pending' ? 'bg-yellow-500/20' : 'bg-white/10'}`}>
          <Icon className="w-6 h-6" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-white">{docInfo.name}</h3>
            <Badge variant="outline" className={`text-xs ${getStatusColor()}`}>
              {getStatusText()}
            </Badge>
          </div>
          <p className="text-sm text-slate-400 mb-3">{docInfo.description}</p>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="border-white/20 text-white hover:bg-white/10"
            data-testid={`upload-btn-${docType}`}
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {status === 'not_uploaded' ? 'Upload Document' : 'Re-upload'}
          </Button>
        </div>

        <div className="flex-shrink-0">
          {getStatusIcon()}
        </div>
      </div>
    </div>
  );
};

const DriverOnboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState(null);

  const [formData, setFormData] = useState({
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: new Date().getFullYear(),
    vehicle_color: '',
    license_plate: '',
  });

  useEffect(() => {
    checkExistingProfile();
  }, []);

  const checkExistingProfile = async () => {
    try {
      const response = await driverAPI.getProfile();
      if (response.data) {
        setHasProfile(true);
        setFormData({
          vehicle_make: response.data.vehicle_make || '',
          vehicle_model: response.data.vehicle_model || '',
          vehicle_year: response.data.vehicle_year || new Date().getFullYear(),
          vehicle_color: response.data.vehicle_color || '',
          license_plate: response.data.license_plate || '',
        });
        setStep(2);
        loadDocuments();
      }
    } catch {
      setHasProfile(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await documentAPI.getVerificationStatus();
      setDocuments(response.data.documents || []);
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleVehicleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await driverAPI.createProfile({
        ...formData,
        vehicle_year: parseInt(formData.vehicle_year),
      });
      toast.success('Vehicle registered successfully!');
      setHasProfile(true);
      setStep(2);
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (docType, fileData, fileName, contentType) => {
    setUploadingDoc(docType);
    try {
      await documentAPI.uploadDocument({
        document_type: docType,
        file_data: fileData,
        file_name: fileName,
        content_type: contentType
      });
      toast.success(`${DOCUMENT_INFO[docType].name} uploaded successfully!`);
      loadDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const getDocumentStatus = (docType) => {
    const doc = documents.find(d => d.document_type === docType);
    return doc?.status || 'not_uploaded';
  };

  const getProgress = () => {
    if (!hasProfile) return 0;
    const uploadedCount = documents.filter(d => d.status && d.status !== 'not_uploaded').length;
    return Math.round(((1 + uploadedCount) / 4) * 100);
  };

  const canProceed = () => {
    if (step === 1) return formData.vehicle_make && formData.vehicle_model && formData.license_plate;
    if (step === 2) {
      const allUploaded = Object.keys(DOCUMENT_INFO).every(
        docType => getDocumentStatus(docType) !== 'not_uploaded'
      );
      return allUploaded;
    }
    return false;
  };

  const handleFinish = () => {
    const allApproved = Object.keys(DOCUMENT_INFO).every(
      docType => getDocumentStatus(docType) === 'approved'
    );

    if (allApproved) {
      toast.success('All documents verified! You can start accepting rides.');
      navigate('/driver');
    } else {
      toast.info('Your documents are under review. You can start accepting rides once approved.');
      navigate('/driver');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Car className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-display font-bold text-white uppercase tracking-tight">
            Driver Onboarding
          </h1>
          <p className="text-slate-400 mt-2">
            Complete your profile to start earning with Reddi
          </p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-slate-400 mb-2">
            <span>Progress</span>
            <span>{getProgress()}% Complete</span>
          </div>
          <Progress value={getProgress()} className="h-2 bg-white/10" />
        </div>

        {/* Step Indicators */}
        <div className="flex justify-center gap-4 mb-8">
          <div
            className={`flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-all ${
              step === 1 ? 'bg-red-600 text-white' : hasProfile ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-slate-400'
            }`}
            onClick={() => hasProfile && setStep(1)}
          >
            {hasProfile ? <CheckCircle className="w-4 h-4" /> : <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm">1</span>}
            <span className="font-medium">Vehicle Info</span>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-600 self-center" />
          <div
            className={`flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-all ${
              step === 2 ? 'bg-red-600 text-white' : 'bg-white/10 text-slate-400'
            }`}
            onClick={() => hasProfile && setStep(2)}
          >
            <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm">2</span>
            <span className="font-medium">Documents</span>
          </div>
        </div>

        {/* Step 1: Vehicle Information */}
        {step === 1 && (
          <Card className="bg-white/5 backdrop-blur-xl border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Vehicle Information</CardTitle>
              <CardDescription className="text-slate-400">
                Enter your vehicle details to get started
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVehicleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Make</Label>
                    <Input
                      name="vehicle_make"
                      placeholder="Toyota"
                      value={formData.vehicle_make}
                      onChange={handleChange}
                      required
                      disabled={hasProfile}
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      data-testid="vehicle-make-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Model</Label>
                    <Input
                      name="vehicle_model"
                      placeholder="Camry"
                      value={formData.vehicle_model}
                      onChange={handleChange}
                      required
                      disabled={hasProfile}
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      data-testid="vehicle-model-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Year</Label>
                    <Input
                      name="vehicle_year"
                      type="number"
                      min="2000"
                      max={new Date().getFullYear() + 1}
                      value={formData.vehicle_year}
                      onChange={handleChange}
                      required
                      disabled={hasProfile}
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      data-testid="vehicle-year-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Color</Label>
                    <Input
                      name="vehicle_color"
                      placeholder="Black"
                      value={formData.vehicle_color}
                      onChange={handleChange}
                      required
                      disabled={hasProfile}
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      data-testid="vehicle-color-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">License Plate</Label>
                  <Input
                    name="license_plate"
                    placeholder="ABC-1234"
                    value={formData.license_plate}
                    onChange={handleChange}
                    required
                    disabled={hasProfile}
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 uppercase"
                    data-testid="license-plate-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  {hasProfile ? (
                    <Button
                      type="button"
                      onClick={() => setStep(2)}
                      className="w-full h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider"
                      data-testid="next-step-btn"
                    >
                      Next: Upload Documents
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={loading || !canProceed()}
                      className="w-full h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider"
                      data-testid="register-vehicle-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          Save & Continue
                          <ChevronRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Document Upload */}
        {step === 2 && (
          <Card className="bg-white/5 backdrop-blur-xl border-white/10">
            <CardHeader>
              <CardTitle className="text-white">Required Documents</CardTitle>
              <CardDescription className="text-slate-400">
                Upload the following documents for verification. Accepted formats: JPEG, PNG, PDF (max 10MB)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(DOCUMENT_INFO).map(([docType, docInfo]) => (
                <DocumentUploadCard
                  key={docType}
                  docType={docType}
                  docInfo={docInfo}
                  status={getDocumentStatus(docType)}
                  onUpload={handleDocumentUpload}
                  uploading={uploadingDoc === docType}
                />
              ))}

              {/* Verification Status Summary */}
              {verificationStatus && (
                <div className={`mt-6 p-4 rounded-lg ${
                  verificationStatus.overall_status === 'approved' ? 'bg-green-500/10 border border-green-500/30' :
                  verificationStatus.overall_status === 'rejected' ? 'bg-red-500/10 border border-red-500/30' :
                  verificationStatus.overall_status === 'pending' ? 'bg-yellow-500/10 border border-yellow-500/30' :
                  'bg-white/5 border border-white/10'
                }`}>
                  <div className="flex items-center gap-3">
                    {verificationStatus.overall_status === 'approved' && <CheckCircle className="w-6 h-6 text-green-400" />}
                    {verificationStatus.overall_status === 'pending' && <AlertCircle className="w-6 h-6 text-yellow-400" />}
                    {verificationStatus.overall_status === 'rejected' && <XCircle className="w-6 h-6 text-red-400" />}
                    {verificationStatus.overall_status === 'incomplete' && <AlertCircle className="w-6 h-6 text-slate-400" />}
                    <div>
                      <p className="font-semibold text-white">
                        {verificationStatus.overall_status === 'approved' && 'All Documents Verified'}
                        {verificationStatus.overall_status === 'pending' && 'Documents Under Review'}
                        {verificationStatus.overall_status === 'rejected' && 'Documents Need Attention'}
                        {verificationStatus.overall_status === 'incomplete' && 'Documents Missing'}
                      </p>
                      <p className="text-sm text-slate-400">
                        {verificationStatus.stats?.approved || 0} approved, {verificationStatus.stats?.pending || 0} pending, {verificationStatus.stats?.rejected || 0} rejected
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 h-12 rounded-full border-white/20 text-white hover:bg-white/10"
                  data-testid="back-btn"
                >
                  <ChevronLeft className="w-5 h-5 mr-2" />
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleFinish}
                  className="flex-1 h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wider"
                  data-testid="finish-onboarding-btn"
                >
                  {canProceed() ? 'Complete Setup' : 'Go to Dashboard'}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Help Text */}
        <p className="text-center text-sm text-slate-500 mt-6">
          Need help? Contact support at support@reddi.com
        </p>
      </div>
    </div>
  );
};

export default DriverOnboarding;
